import type {
    VoteEntity,
    VoteResult,
    VoterInfo,
} from "@/appInterface/windows/VoteWindow/VoteResults/types";
import { canv } from "@/canv/global";
import type {
    CreateOrDeleteVoteProperty,
    PropertyBuildEventAction,
    PropertyBuildEventStructure,
    PropertyEventStructure,
} from "@/eventManager/events/types";

export const getVoteStats = (
    voteInfo: VoteEntity,
    myUserToken: string,
): { myVoteCount: number; totalCandidates: number } => {
    let myVoteCount = 0;

    for (const child of Object.values(voteInfo.children)) {
        child.votes.forEach((vote) => {
            if (vote.token === myUserToken) {
                myVoteCount += 1;
            }
        });
    }

    const totalCandidates = Object.keys(voteInfo.children).length;

    return { myVoteCount, totalCandidates };
};

export const getFinishEventData = (UID: string): PropertyEventStructure[] => {
    return [
        {
            type: "property",
            UID: UID,
            fixType: "status",
            state: {
                isResolved: true,
            },
        },
    ];
};

export const getAddDurationEventData = (
    UID: string,
    duration: number,
): PropertyEventStructure[] => {
    return [
        {
            type: "property",
            UID: UID,
            fixType: "status",
            state: {
                additionalDuration: duration,
            },
        },
    ];
};

export const getAddOrRemoveVoteEventData = (
    UID: string,
    widgetUID: string,
    action: PropertyBuildEventAction,
    userToken: string,
    accountName: string,
): PropertyBuildEventStructure[] => {
    const property: CreateOrDeleteVoteProperty = { childUID: widgetUID, token: userToken };

    if (action === "add") {
        property.accountName = accountName;
    }

    return [
        {
            type: "propertyBuild",
            UID,
            fixType: "votes",
            action,
            property,
        },
    ];
};

export const getAddOrDeleteVoterVoteEventData = (
    UID: string,
    action: PropertyBuildEventAction,
    token: string,
    accountName: string,
): PropertyBuildEventStructure[] => {
    return [
        {
            type: "propertyBuild",
            UID,
            fixType: "participants",
            action,
            property: {
                token,
                accountName,
            },
        },
    ];
};

export const getMembersVoteData = (vote: VoteEntity): VoteResult => {
    const data = vote;
    if (data) {
        const votersMap = new Map<
            string,
            {
                accountName: string;
                token: string;
                votesCount: number;
            }
        >();

        vote.participants.forEach((participant) => {
            votersMap.set(participant.token, {
                accountName: participant.accountName,
                token: participant.token,
                votesCount: 0,
            });
        });

        Object.values(data.children || []).forEach((child) => {
            child.votes.forEach((vote) => {
                const existing = votersMap.get(vote.token);
                if (existing) {
                    existing.votesCount += 1;
                } else {
                    votersMap.set(vote.token, {
                        accountName: vote.accountName,
                        token: vote.token,
                        votesCount: 1,
                    });
                }
            });
        });
        const voters: VoterInfo[] = Array.from(votersMap.values()).map((voter) => {
            const voted = data.oneVotePerItem
                ? voter.votesCount >= 1
                : voter.votesCount >= data.votePerAccount;
            return {
                accountName: voter.accountName,
                token: voter.token,
                votesCount: voter.votesCount,
                voted,
            };
        });

        return {
            voters,
            totalParticipants: voters.length,
            totalVoted: voters.filter((v) => v.voted).length,
        };
    }

    return { voters: [], totalParticipants: 0, totalVoted: 0 };
};
